import { ReactNode, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { HpKpi, HpParams, useHighProbabilityRoi } from '../../queries/racingAnalytics';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Spinner } from '../../components/ui/Spinner';
import { Callout } from '../../components/ui/Callout';
import { cn } from '../../lib/utils';

const AXIS = { tick: { fontSize: 11, fill: 'hsl(var(--muted-foreground))' }, stroke: 'hsl(var(--border))' };
const TOOLTIP_STYLE = { fontSize: 12, background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--popover-foreground))' };

const DAYS = [
  { n: 1, label: 'Mon' }, { n: 2, label: 'Tue' }, { n: 3, label: 'Wed' }, { n: 4, label: 'Thu' },
  { n: 5, label: 'Fri' }, { n: 6, label: 'Sat' }, { n: 7, label: 'Sun' },
];

type Period = '7d' | '28d' | '90d' | 'all' | 'custom';
const PERIODS: { value: Period; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '28d', label: 'Last 28 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom' },
];

const fmtDate = (d: Date) => d.toISOString().slice(0, 10);

// Preset → concrete date range. 'all' returns an empty start (all time).
function resolveRange(period: Period, customStart: string, customEnd: string): { startDate?: string; endDate?: string } {
  if (period === 'custom') return { startDate: customStart || undefined, endDate: customEnd || undefined };
  if (period === 'all') return {};
  const days = period === '7d' ? 7 : period === '28d' ? 28 : 90;
  const start = new Date();
  start.setDate(start.getDate() - days);
  return { startDate: fmtDate(start), endDate: fmtDate(new Date()) };
}

const pctText = (v: number | null) => (v == null ? 'n/a' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`);
const pctClass = (v: number | null) =>
  v == null ? 'text-muted-foreground' : v > 0 ? 'text-emerald-600 dark:text-emerald-400' : v < 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground';
const money = (v: number) => `$${v.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function KpiCard({ title, k }: { title: string; k: HpKpi }) {
  const row = (label: string, node: ReactNode) => (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{node}</span>
    </div>
  );
  return (
    <Card>
      <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardContent className="space-y-1 pt-0">
        {row('Bets', k.bets.toLocaleString())}
        {row('Win strike', `${k.winStrike.toFixed(1)}%`)}
        {row('Place strike', k.placeStrike == null ? 'n/a' : `${k.placeStrike.toFixed(1)}%`)}
        {row('ROI (win, market)', <span className={pctClass(k.roiWinMarket)}>{pctText(k.roiWinMarket)}</span>)}
        {row('ROI (win, SP)', <span className={pctClass(k.roiWinSp)}>{pctText(k.roiWinSp)}</span>)}
        {row('ROI (E/W, market)', <span className={pctClass(k.roiEwMarket)}>{pctText(k.roiEwMarket)}</span>)}
        {row('Staked', money(k.staked))}
        {row('Profit (market)', <span className={pctClass(k.profitMarket)}>{money(k.profitMarket)}</span>)}
        {row('Profit (SP)', <span className={pctClass(k.profitSp)}>{money(k.profitSp)}</span>)}
      </CardContent>
    </Card>
  );
}

export function HighProbabilityRoiTab() {
  const [params, setParams] = useState<HpParams>({
    unitValue: 10, topWin: 1, topPlace: 0, valueWin: 1, valuePlace: 0, days: [1, 2, 3, 4, 5, 6, 7],
  });
  const [period, setPeriod] = useState<Period>('28d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const range = resolveRange(period, customStart, customEnd);
  const { data, isLoading } = useHighProbabilityRoi({ ...params, startDate: range.startDate, endDate: range.endDate });
  const set = (patch: Partial<HpParams>) => setParams((p) => ({ ...p, ...patch }));
  const toggleDay = (n: number) =>
    setParams((p) => ({ ...p, days: p.days.includes(n) ? p.days.filter((d) => d !== n) : [...p.days, n].sort() }));

  const numInput = 'w-16 rounded-md border border-input bg-background px-2 py-1 text-sm shadow-sm';

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        A staking calculator over the pre-aggregated high-probability picks. Set unit stakes and win/place splits to model returns.
      </p>

      <div className="flex flex-wrap gap-1">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => setPeriod(p.value)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              period === p.value ? 'bg-primary text-primary-foreground shadow-sm' : 'border border-input text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-4 rounded-xl border border-border bg-card p-4 shadow-sm">
        <label className="text-sm">
          Unit value ($)
          <input type="number" min={1} className={`${numInput} mt-1 block`} value={params.unitValue} onChange={(e) => set({ unitValue: Number(e.target.value) })} />
        </label>
        <div className="text-sm">
          <div className="mb-1">Top units (win / place)</div>
          <input type="number" min={0} step={0.5} className={numInput} value={params.topWin} onChange={(e) => set({ topWin: Number(e.target.value) })} />
          <span className="mx-1">/</span>
          <input type="number" min={0} step={0.5} className={numInput} value={params.topPlace} onChange={(e) => set({ topPlace: Number(e.target.value) })} />
        </div>
        <div className="text-sm">
          <div className="mb-1">Value units (win / place)</div>
          <input type="number" min={0} step={0.5} className={numInput} value={params.valueWin} onChange={(e) => set({ valueWin: Number(e.target.value) })} />
          <span className="mx-1">/</span>
          <input type="number" min={0} step={0.5} className={numInput} value={params.valuePlace} onChange={(e) => set({ valuePlace: Number(e.target.value) })} />
        </div>
        <div className="text-sm">
          <div className="mb-1">Days</div>
          <div className="flex gap-1">
            {DAYS.map((d) => (
              <button
                key={d.n}
                type="button"
                onClick={() => toggleDay(d.n)}
                className={cn(
                  'rounded px-2 py-1 text-xs font-medium transition-colors',
                  params.days.includes(d.n) ? 'bg-primary text-primary-foreground' : 'border border-input text-muted-foreground hover:bg-accent',
                )}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
        {period === 'custom' && (
          <>
            <label className="text-sm">
              From
              <input type="date" className={`${numInput.replace('w-16', 'w-36')} mt-1 block`} value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
            </label>
            <label className="text-sm">
              To
              <input type="date" className={`${numInput.replace('w-16', 'w-36')} mt-1 block`} value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
            </label>
          </>
        )}
      </div>

      {isLoading || !data ? (
        <Spinner />
      ) : (
        <>
          {data.spansValueRuleChange && (
            <Callout variant="warning" title={`Value rule changed ${data.valueRuleChangeDate}.`}>
              The qualifying margin tightened from $2 to $1 of the race&rsquo;s lowest assessed odds. Selections before that date
              keep the $2 rule they were published under, so this window mixes both definitions — expect fewer value picks, and a
              higher strike rate and ROI, after the change.
            </Callout>
          )}
          {!data.placeAvailable && (
            <p className="text-xs text-muted-foreground">Place / Each-Way ROI needs recorded place dividends (from {data.placeDataStartDate}); shown as n/a where unavailable.</p>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <KpiCard title="Top picks" k={data.kpis.top} />
            <KpiCard title="Value picks" k={data.kpis.value} />
            <KpiCard title="Combined" k={data.kpis.combined} />
          </div>

          <Card>
            <CardHeader><CardTitle>Cumulative P&amp;L ({money(data.unitValue)}/unit) — market vs SP</CardTitle></CardHeader>
            <CardContent className="pt-0">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" interval="preserveStartEnd" {...AXIS} />
                    <YAxis {...AXIS} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ stroke: 'hsl(var(--border))' }} formatter={(v: number) => money(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" dataKey="combined_cum_market" name="Market" stroke="#3B82F6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="combined_cum_sp" name="Starting price" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
